import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Lock, ArrowLeft, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function ChangePassword() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    if (next !== confirm) {
      toast.error("New passwords do not match");
      return;
    }
    if (next === current) {
      toast.error("New password must be different from current");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated successfully");
      setCurrent("");
      setNext("");
      setConfirm("");
      setTimeout(() => nav("/admin"), 800);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to update password";
      toast.error(typeof msg === "string" ? msg : "Failed to update password");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full pl-10 pr-4 py-3 bg-white border border-neutral-300 rounded-md text-brand-charcoal text-sm focus:outline-none focus:ring-2 focus:ring-brand-gold focus:border-brand-gold";

  return (
    <div data-testid="change-password-page" className="min-h-[calc(100vh-80px)] bg-brand-cream py-12">
      <div className="max-w-xl mx-auto px-4">
        <Link to="/admin" data-testid="cp-back-link" className="inline-flex items-center gap-2 text-brand-charcoal/70 hover:text-brand-green text-sm font-semibold mb-6">
          <ArrowLeft className="w-4 h-4" /> Back to Dashboard
        </Link>

        <div className="bg-white rounded-lg border border-neutral-200 shadow-md overflow-hidden">
          <div className="bg-brand-green px-8 py-6">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-md bg-brand-gold/20 flex items-center justify-center">
                <ShieldCheck className="w-5 h-5 text-brand-gold" />
              </div>
              <div>
                <h1 className="font-display font-bold text-white text-xl">Change Password</h1>
                <p className="text-white/70 text-xs mt-0.5">Signed in as {user?.email}</p>
              </div>
            </div>
          </div>

          <form onSubmit={submit} data-testid="change-password-form" className="p-8 space-y-5">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-brand-charcoal">Current Password</label>
              <div className="relative mt-1.5">
                <Lock className="w-4 h-4 text-neutral-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  data-testid="cp-current"
                  type="password"
                  required
                  value={current}
                  onChange={(e) => setCurrent(e.target.value)}
                  className={inputCls}
                  autoComplete="current-password"
                />
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-brand-charcoal">New Password</label>
              <div className="relative mt-1.5">
                <Lock className="w-4 h-4 text-neutral-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  data-testid="cp-new"
                  type="password"
                  required
                  minLength={8}
                  value={next}
                  onChange={(e) => setNext(e.target.value)}
                  className={inputCls}
                  autoComplete="new-password"
                />
              </div>
              <p className="text-xs text-brand-charcoal/50 mt-1.5">Minimum 8 characters. Use a mix of letters, numbers, and symbols.</p>
            </div>

            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-brand-charcoal">Confirm New Password</label>
              <div className="relative mt-1.5">
                <Lock className="w-4 h-4 text-neutral-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  data-testid="cp-confirm"
                  type="password"
                  required
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className={inputCls}
                  autoComplete="new-password"
                />
              </div>
            </div>

            <div className="pt-3 flex flex-wrap gap-3">
              <button
                type="submit"
                disabled={loading}
                data-testid="cp-submit"
                className="btn-green disabled:opacity-60"
                style={{ padding: "0.75rem 1.5rem", fontSize: "0.9rem" }}
              >
                {loading ? "Updating…" : "Update Password"}
              </button>
              <Link to="/admin" data-testid="cp-cancel" className="btn-outline-green" style={{ padding: "0.75rem 1.5rem", fontSize: "0.9rem" }}>
                Cancel
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
